import { useCallback, useEffect, useMemo, useState } from "react";

import { useActionHook, useModals } from "hooks";
import { useDealActions, useDocumentsActions } from "store";
import { DealBase, IFolder } from "store/types";

// import { IMoveDocumentData } from "store/types";

interface UseSelectDealFolderHookProps {
  onSubmit: (data: { deal: DealBase | null; selectedFolder: IFolder | null }) => void;
  preselectedDeal?: DealBase | null;
  onClose?: () => void;
  isGeneralDocument?: boolean;
}

export const useSelectDealFolderHook = ({
  onSubmit,
  preselectedDeal,
  onClose,
  isGeneralDocument
}: UseSelectDealFolderHookProps) => {
  const { closeSelectDealFolderModal, errorModal, createModal } = useModals();
  const { getDealFoldersAction } = useDocumentsActions();
  const { createFolderAction } = useDealActions();
  const [deal, setDeal] = useState<DealBase | null>(null);
  const [step, setStep] = useState<"select-deal" | "select-folder">("select-deal");
  const [selectedFolder, setSelectedFolder] = useState<IFolder | null>(null);

  const onCloseModal = () => {
    closeSelectDealFolderModal();
    setDeal(null);
    setStep("select-deal");
    onClose?.();
  };

  const {
    action: getDealFolders,
    loading,
    data: foldersWithKeys,
    setData: setFoldersWithKeys
  } = useActionHook(getDealFoldersAction, {
    onReject: () => {
      errorModal({ msg: "Something went wrong" });
    }
  });

  const { action: submit } = useActionHook(onSubmit, {
    onSuccess: () => {
      setSelectedFolder(null);
      onCloseModal();
    },
    onReject: () => {
      errorModal({ msg: "Something went wrong" });
    }
  });

  const hasNewFolder = useMemo(() => {
    if (loading && !foldersWithKeys) return false;
    return foldersWithKeys?.[0]?.id === 0;
  }, [foldersWithKeys]);

  const transformFoldersToTree = (foldersWithKeys: Record<number, IFolder>): IFolder[] => {
    const foldersArray = Object.values(foldersWithKeys);
    const result: IFolder[] = [];

    const newFolder = foldersArray.find((folder) => folder.id === 0);

    const rootFolders = foldersArray.filter((folder) => !folder.parent_id && folder.id !== 0);

    const buildFolderTree = (folder: IFolder): IFolder => {
      const children = foldersArray.filter((f) => f.parent_id === folder.id && f.id !== 0).map(buildFolderTree);

      return {
        ...folder,
        children: children.length ? children : null
      };
    };

    rootFolders.forEach((rootFolder) => {
      result.push(buildFolderTree(rootFolder));
    });

    if (newFolder) {
      if (newFolder.parent_id) {
        const updateParentFolder = (folders: IFolder[]): IFolder[] => {
          //if new folder has parent_id, find parent folder and add to its children
          return folders.map((folder) => {
            if (folder.id === newFolder.parent_id) {
              const hasNewFolder = folder.children?.some((child) => child.id === 0);
              if (hasNewFolder) {
                return folder;
              }

              return {
                ...folder,
                children: [...(folder.children || []), { ...newFolder, children: null }]
              };
            }
            if (folder.children) {
              return {
                ...folder,
                children: updateParentFolder(folder.children)
              };
            }
            return folder;
          });
        };
        return updateParentFolder(result);
      } else {
        const hasRootNewFolder = result.some((folder) => folder.id === 0);
        if (!hasRootNewFolder) {
          result.push({ ...newFolder, children: null });
        }
      }
    }

    return result;
  };

  const createNewFolderHandler = useCallback(
    (title: string) => {
      const newFolder = {
        id: 0,
        title,
        parent_id: selectedFolder?.id || null,
        children: null
      } as IFolder;

      if (selectedFolder) {
        setFoldersWithKeys((prev) => ({
          ...prev,
          [selectedFolder.id]: {
            ...prev?.[selectedFolder.id],
            children: [...(prev?.[selectedFolder.id]?.children || []), newFolder]
          } as IFolder,
          [newFolder.id]: newFolder
        }));
      } else {
        setFoldersWithKeys((prev) => ({
          ...prev,
          [newFolder.id]: newFolder
        }));
      }
    },
    [selectedFolder]
  );

  const onCreateNewFolder = useCallback(() => {
    if (!deal) return;
    if (isGeneralDocument) {
      createModal({
        action: async (title: string) => {
          createNewFolderHandler(title);
          setSelectedFolder({ id: 0, title, parent_id: selectedFolder?.id || null, children: null } as IFolder);
        },
        title: "Create New Folder",
        submitButtonTitle: "Create",
        label: "Folder name"
      });
      return;
    }
    createModal({
      action: async (title: string) => {
        if (!deal) return;
        await createFolderAction({ dealId: deal.id, title, parent_id: selectedFolder?.id || null });
        const result = await getDealFolders({ dealId: deal.id, kind: "folder" });
        const newFolder = result && Object.values(result).find((f) => f.title === title);
        if (newFolder) setSelectedFolder(newFolder);
      },
      title: "Create New Folder",
      submitButtonTitle: "Create",
      label: "Folder name"
    });
  }, [
    deal,
    createModal,
    createNewFolderHandler,
    selectedFolder,
    isGeneralDocument,
    getDealFolders,
    createFolderAction
  ]);

  const handleOutsideUnselect = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest("[data-create-folder]")) {
      return;
    }

    if ((e.target as HTMLElement).closest("[data-move-document-submit]")) {
      return;
    }

    if (!(e.target as HTMLElement).closest("[data-folder]")) {
      setSelectedFolder(null);
    }
  };

  const onSelectHandle = useCallback(() => {
    if (step === "select-deal" && deal?.id) {
      getDealFolders({ dealId: deal.id, kind: "folder" });
      setStep("select-folder");
      return;
    }

    if (step === "select-folder" && selectedFolder && deal) {
      submit({
        deal,
        selectedFolder
      });
    }
  }, [step, deal, selectedFolder, submit]);

  const folders = useMemo(() => transformFoldersToTree(foldersWithKeys || {}), [foldersWithKeys]);

  const isSelectDisabled = useMemo(() => {
    if (step === "select-deal" && !deal) return true;
    if (step === "select-folder" && !selectedFolder) return true;
    return false;
  }, [deal, step, selectedFolder]);

  useEffect(() => {
    if (preselectedDeal) {
      setDeal(preselectedDeal);
      setStep("select-folder");
      getDealFolders({ dealId: preselectedDeal.id, kind: "folder" });
    }
  }, [preselectedDeal]);

  return {
    hasNewFolder,
    deal,
    setDeal,
    step,
    setStep,
    selectedFolder,
    setSelectedFolder,
    loading,
    folders,
    handleOutsideUnselect,
    onCreateNewFolder,
    onSelectHandle,
    onCloseModal,
    isSelectDisabled
  };
};
